import { useEffect, useState } from "react";
import { ArrowRight, ChartBar, CheckCircle, Clock, MagnifyingGlass, ShieldCheck, TrendUp, UsersThree } from "@phosphor-icons/react";

const benefits = [
  { Icon: MagnifyingGlass, number: "01", title: "Get found", text: "Show up when the right customers are looking for exactly what you do." },
  { Icon: Clock, number: "02", title: "Save time", text: "We handle the work behind your growth, so you can spend more time running your business." },
  { Icon: ShieldCheck, number: "03", title: "Build trust", text: "Create a consistent presence that makes choosing your business feel easy." },
];

const boltRankingSteps = [12, 4, 2];
// Force every ranking frame to use the SAME founder-approved image release.
// Avoid old cached frames appearing alongside the latest #12/#4/#2 assets.
const heroAssetVersion = "final-dark-caption-1293dd0";
const heroImageUrl = (rank) => `/images/ranktrust-approved-hero-${rank}.png?v=${heroAssetVersion}`;

export function BoltHero() {
  const [rankingStep, setRankingStep] = useState(0);

  // Pre-decode every frame before animating to avoid image-request/repaint flashes.
  // Each frame is mounted once at the exact same dimensions. Only opacity changes.
  useEffect(() => {
    let active = true;
    let intervalId;
    Promise.all(boltRankingSteps.map((rank) => {
      const img = new Image();
      img.src = heroImageUrl(rank);
      return img.decode ? img.decode() : new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
    })).then(() => {
      if (!active) return;
      intervalId = window.setInterval(() => {
        setRankingStep((currentStep) => (currentStep + 1) % boltRankingSteps.length);
      }, 2000);
    }).catch(() => {
      // Hold the first approved frame if an image cannot be decoded.
      // Never flash incomplete artwork or cycle through missing frames.
    });
    return () => {
      active = false;
      window.clearInterval(intervalId);
    };
  }, []);

  return (
    <>
      {/* Founder-approved November 4 hero. Actual nav is rendered separately.
          Final image frames are 1672x730; all three are identical except the ranking badge.
          Fit the entire artwork within the available desktop viewport height.
          Never crop the caption, independently resize the phone, or duplicate the navbar. */}
      <section className="hidden lg:block relative overflow-hidden text-white"
        style={{ background: "linear-gradient(110deg, #051a34 0%, #07407a 65%, #0b7edc 100%)" }}
        aria-label="RankTrust business growth introduction">
        <div className="relative mx-auto"
          style={{
            width: "min(100%, calc((100svh - 86px) * 2.29041))",
            aspectRatio: "1672 / 730",
          }}>
          {boltRankingSteps.map((rank, index) => (
            <img
              key={rank}
              src={heroImageUrl(rank)}
              alt={index === 0 ? "RankTrust: Get found. Win trust. Grow with confidence." : ""}
              aria-hidden={index !== 0}
              className="absolute inset-0 block w-full h-full"
              style={{ objectFit: "contain", opacity: rankingStep === index ? 1 : 0, pointerEvents: "none" }}
              decoding="async"
            />
          ))}
          <h1 className="sr-only">Get found. Win trust. Grow with confidence.</h1>
          {/* Click targets are positioned against the final cropped hero, never viewport. */}
          <a href="https://calendly.com/intentbrain/ranktrust-io" target="_blank" rel="noopener noreferrer"
            aria-label="Book My Growth Assessment"
            className="absolute z-10 left-[7.8%] top-[84%] w-[26.2%] h-[9.8%] focus-visible:outline focus-visible:outline-4 focus-visible:outline-white" />
          <a href="#how-it-works" aria-label="How it works"
            className="absolute z-10 left-[36.3%] top-[84%] w-[12%] h-[9.8%] focus-visible:outline focus-visible:outline-4 focus-visible:outline-white" />
        </div>
      </section>
      {/* The previously working small-screen layout is preserved. */}
    <section className="relative overflow-hidden bg-[#0c2545] text-white lg:hidden" aria-labelledby="bolt-hero-title">
      {/* The approved Bolt illustration is a cropped asset: no baked navigation, headline, or clickable controls. */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-10 lg:py-0 lg:min-h-[740px] flex flex-col justify-center">
        <div className="text-center lg:text-left lg:max-w-[51%]">
          <p className="text-[#6abaff] uppercase tracking-[0.18em] text-xs font-bold mb-6">For service businesses &amp; professional services</p>
          <h1 id="bolt-hero-title" className="font-black tracking-tight leading-[1.04] mb-7" style={{fontSize:"clamp(3rem, 5.6vw, 5.25rem)"}}>Get found. <span className="text-[#55b7ff]">Win trust.</span><br />Grow with confidence.</h1>
          <p className="text-lg text-[#d0e0f1] leading-relaxed max-w-xl mx-auto lg:mx-0 mb-9">Help the right people choose you, protect the business you’ve built, and spend less time wondering what to do next.</p>
          <div className="flex flex-wrap gap-6 items-center justify-center lg:justify-start mb-7">
            <a href="https://calendly.com/intentbrain/ranktrust-io" target="_blank" rel="noopener noreferrer" className="inline-flex gap-3 items-center bg-[#1688ed] hover:bg-[#2a99fb] text-white font-bold px-7 py-4 rounded-lg transition-colors">Book My Growth Assessment <ArrowRight size={20} aria-hidden="true" /></a>
            <a href="#how-it-works" className="inline-flex gap-2 items-center font-semibold text-white hover:text-[#6abaff]">How it works <ArrowRight size={18} aria-hidden="true" /></a>
          </div>
          <p className="text-sm text-[#bbd2e8] flex gap-2 items-center justify-center lg:justify-start"><CheckCircle size={17} aria-hidden="true" /> A focused 10-minute conversation. No pressure, no jargon.</p>
        </div>
      </div>
      <div className="relative z-0 w-full lg:absolute lg:inset-y-0 lg:right-0 lg:w-[58vw] lg:max-w-[880px] lg:flex lg:items-center" aria-label="Illustrative RidgeLine Roofing Google Maps ranking progression: number 12, then 4, then 2, changing every two seconds">
        <style>{`
          @keyframes rtBoltRanking { 0%, 31% { opacity:1; visibility:visible; } 33.333%, 98% { opacity:0; visibility:hidden; } 100% { opacity:1; visibility:visible; } }
          .rt-bolt-rank-slide { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; border-radius:7px; animation:rtBoltRanking 6s linear infinite; opacity:0; }
          .rt-bolt-rank-slide:nth-child(2) { animation-delay:-4s; }
          .rt-bolt-rank-slide:nth-child(3) { animation-delay:-2s; }
          @media(prefers-reduced-motion:reduce) { .rt-bolt-rank-slide { animation:none!important; opacity:0; visibility:hidden; } .rt-bolt-rank-slide:nth-child(3) { opacity:1; visibility:visible; } }
        `}</style>
        <div className="relative w-full" style={{maskImage:"linear-gradient(90deg, transparent 0%, black 8%, black 100%)"}}>
          <img src="/images/ranktrust-bolt-approved-right-hero.webp" alt="Blue circular motion behind a roofing contractor pin, a tilted Maps phone, and the caption The Done-For-You details other agencies miss." className="block w-full h-auto" decoding="async" />
          {/* Coordinates follow the approved image's original ranking overlay, adjusted for the right-side crop. */}
          <div className="absolute left-[62.3%] top-[45.9%] w-[9.1%] h-[8.7%] rounded-[8px] ring-2 ring-white shadow-[0_0_14px_rgba(22,141,245,.65)] rotate-[5deg]" aria-hidden="true">
            {[12,4,2].map((rank)=>(
              <div key={rank} className="rt-bolt-rank-slide text-white font-extrabold text-[clamp(12px,1.8vw,24px)]" style={{background:rank===12?"#d7334b":rank===4?"#147fe1":"#0b9e64"}}>
                #{rank}
                {rank!==12 && (
                  <svg className="absolute -right-[80%] -top-[74%] w-[95%] h-[114%] overflow-visible drop-shadow-[0_2px_4px_rgba(0,69,155,.55)]" viewBox="0 0 70 70" aria-hidden="true">
                    <path d="M5 62 Q26 56 47 22" fill="none" stroke="white" strokeWidth="10" strokeLinecap="round"/>
                    <path d="M5 62 Q26 56 47 22" fill="none" stroke="#1688ed" strokeWidth="6" strokeLinecap="round"/>
                    <path d="M37 14 L64 5 L57 32 Z" fill="#1688ed" stroke="white" strokeWidth="3" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

export function BoltBenefits() {
  return (
    <section id="why-ranktrust" className="bg-[#f6f8fc] text-[#10233f] py-20 md:py-28" aria-labelledby="bolt-benefits-title">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[#245DDF] font-bold uppercase tracking-[0.16em] text-xs mb-5">Less guessing. More momentum.</p>
        <div className="grid md:grid-cols-2 gap-7 md:gap-16 items-end mb-12">
          <h2 id="bolt-benefits-title" className="text-4xl md:text-5xl font-black leading-tight tracking-tight">Good businesses get overlooked <span className="text-[#245DDF]">every day.</span></h2>
          <p className="text-lg text-slate-600 leading-relaxed">You already do great work. RankTrust helps more people see it, believe it, and take the next step.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {benefits.map(({Icon,number,title,text})=>(
            <article key={title} className="bg-white border border-[#e0e8f2] rounded-xl p-8 shadow-sm">
              <div className="flex justify-between items-center mb-9"><span className="w-12 h-12 rounded-xl bg-[#eaf4ff] text-[#1688ed] grid place-items-center"><Icon size={24} aria-hidden="true" /></span><span className="text-[#8aa2bb] font-bold">{number}</span></div>
              <h3 className="text-2xl font-bold mb-3">{title}</h3><p className="text-slate-600 leading-relaxed mb-6">{text}</p>
              <a href="#how-it-works" className="inline-flex items-center gap-2 text-[#245DDF] font-bold">Explore <ArrowRight size={17} aria-hidden="true" /></a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
